import { isOAuthCallbackRoute } from 'helpers/authCallback';

interface IDataLayerEvent extends Record<string, unknown> {
  event: string;
}

interface ITrackAuthenticationSuccessParams {
  accountCreated: boolean;
  method: string;
  orgId?: string | null;
  orgCreatedAt?: string | null;
}

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
  }
}

export const pushDataLayerEvent = (dataLayerEvent: IDataLayerEvent): void => {
  if (typeof window === 'undefined') {
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(dataLayerEvent);
};

const shouldQueueCanonicalSignupEvent = (): boolean => {
  if (typeof window === 'undefined' || !isOAuthCallbackRoute()) {
    return false;
  }

  const analyticsWindow = window as Window & {
    analytics?: { track?: unknown };
  };

  return typeof analyticsWindow.analytics?.track !== 'function';
};

export const trackAuthenticationSuccess = ({
  accountCreated,
  method,
  orgId,
  orgCreatedAt
}: ITrackAuthenticationSuccessParams): void => {
  if (!accountCreated) {
    pushDataLayerEvent({ event: 'login', method });
    return;
  }

  pushDataLayerEvent({
    // OAuth callbacks intentionally defer Segment and GTM. Queue the exact
    // event name consumed by the live GTM signup trigger for the later SPA
    // bootstrap; retain the existing path wherever Segment can deliver it.
    event: shouldQueueCanonicalSignupEvent() ? 'SIGNUP' : 'sign_up',
    method,
    ...(orgId && orgCreatedAt
      ? { org_id: orgId, org_created_at: orgCreatedAt }
      : {})
  });

  // The backend owns this semantic contract: it returns both fields only
  // when this authentication transaction creates a new organization.
  if (orgId && orgCreatedAt) {
    pushDataLayerEvent({
      event: 'sign_up_completed',
      org_id: orgId,
      org_created_at: orgCreatedAt
    });
  }
};
